import { BASE_URL, expiresInSeconds, routePathsByRole } from "../../../constants";
import { setCredentials } from "../../../store/actions/authActions";
import { fetchEntitlement } from "../../../store/actions/entitlementActions";
import { fetchUserByEmail } from "../../../store/actions/userActions";
import { isImpersonating } from "../../../auth/impersonation";

const post = async (path, body) => {
  const res = await fetch(`${BASE_URL}/api/users/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  let data = {};
  try { data = await res.json(); } catch { data = {}; }
  return { ok: res.ok, status: res.status, data };
};

export const sendSignupCode = ({ email, phone, channel }) => post("auth/signup-code/send/", { email, phone, channel });
export const verifySignupCode = ({ email, code }) => post("auth/signup-code/verify/", { email, code });
export const registerAccount = (payload) => post("register/", payload);
export const loginWithPassword = ({ email, password }) => post("login/", { email, password });
export const sendPhoneLoginCode = ({ phone }) => post("auth/phone-login/send/", { phone });
export const verifyPhoneLoginCode = ({ phone, code }) => post("auth/phone-login/verify/", { phone, code });
export const googleAuth = ({ accessToken }) => post("auth/google/", { accessToken });
// Legacy link-flow accounts only; V2 sign-ups confirm a code instead.
export const resendVerificationEmail = ({ email }) => post("verify-email/resend/", { email });

// Google accounts never type a password; one is generated so the record is
// complete. They sign in with Google, or set their own through Forgot password.
export const generatedPassword = () => {
  const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return `${Array.from(bytes, (b) => chars[b % chars.length]).join("")}A1!`;
};

// Store a login response the way the password login does, so every later
// request and route guard sees the same session.
export const startSession = async (data, dispatch) => {
  const expiresAt = Date.now() + expiresInSeconds * 1000;
  localStorage.setItem("token", data.access);
  localStorage.setItem("myEmail", data.email);
  localStorage.setItem("expiresAt", expiresAt);
  sessionStorage.removeItem("athleteUpgradeSeen");
  // Start every per-user slice over (athlete doc, entitlement, user lists)
  // even when the last session ended without the logout action, for example
  // another tab cleared the keys. The shared code lists survive; the new
  // token is set right after, so nothing here clears it.
  dispatch({ type: "USER_LOGOUT" });
  dispatch(setCredentials({ token: data.access, user: data.user || null }));
  // Load the user record now so route guards see the permissions without a reload.
  try { await dispatch(fetchUserByEmail(data.email)); } catch { /* the guards retry */ }
  try { await dispatch(fetchEntitlement()); } catch { /* entitlement is best effort */ }
};

const normEmail = (value) => (value || "").trim().toLowerCase();

// True when a storage event from another tab ends the session this tab is
// using: the token was removed, storage was cleared, or the stored email
// became a different account. The event's old value is what this tab was
// running as. A new token for the same account, or a sign-in in a tab that
// had no session, ends nothing.
export const otherTabEndedSession = (event) => {
  if (!event) return false;
  if (event.storageArea && event.storageArea !== window.localStorage) return false;
  if (event.key === null) return !localStorage.getItem("token");
  if (event.key === "token") return !!event.oldValue && !event.newValue;
  if (event.key === "myEmail") {
    const before = normEmail(event.oldValue);
    return !!before && before !== normEmail(event.newValue);
  }
  return false;
};

// localStorage is shared by every tab of the site, so a log out or a log in
// as someone else in one tab rewrites the token the others send. This tab
// then reloads (onEnded), so it never shows one account while acting as
// another; a protected page lands on the log in screen. Admin "open as user"
// tabs keep their own per-tab session and are left alone. Returns the stop
// function.
export const watchSessionAcrossTabs = (onEnded = () => window.location.reload()) => {
  if (isImpersonating()) return () => {};
  let ended = false;
  const onStorage = (event) => {
    if (ended || isImpersonating() || !otherTabEndedSession(event)) return;
    ended = true;
    onEnded();
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
};

export const homePathFor = (permissions = []) => {
  for (const role of Object.keys(routePathsByRole)) {
    if (permissions.includes(role)) return routePathsByRole[role].url;
  }
  return "/";
};

// Kept here for existing importers; the phone screens use ./phoneFormat.
export { formatPhone, usPhoneDigits } from "./phoneFormat";
